import apiClient from './apiService';

export type InvestigationStatus = 'OPEN' | 'CLOSED';

export interface CapaInvestigation {
    id: number;
    entity_id: number;
    issue_id: number;
    owner_user_id: number;
    code_seq?: number | null;
    status: InvestigationStatus;
    due_date?: string | null;
    opened_on: string;
    closed_on?: string | null;
    closed_by?: number | null;
    is_locked?: boolean;
    locked_on?: string | null;
    locked_by?: number | null;
    is_active: number;
    // Enrichments
    code?: string | null;        // own INV-N code (icodegen)
    issue_code?: string;
    issue_name?: string;
    issue_description?: string;
    owner_name?: string | null;
    created_by_name?: string | null;
    owner_login_id?: string | null;
    team_count?: number;
    impact_count?: number;
    rca_count?: number;
    task_count?: number;
    triage_outcome?: string | null;
}

export interface InvestigationCreate {
    owner_user_id: number;
    due_date?: string | null;
}

export interface InvestigationOwnerTransfer {
    owner_user_id: number;
}

export interface CapaTeamMember {
    id: number;
    investigation_id: number;
    user_id: number;
    user_name?: string;
    first_name?: string;
    last_name?: string;
    login_id?: string;
    expected_action?: 'Responsible' | 'Accountable' | 'Consulted' | 'Informed' | 'Investigator' | null;
    position_id?: number | null;
    authority_id?: number | null;
    location_id?: number | null;
    is_active: number;
}

export interface TeamMemberCreate {
    user_id: number;
    expected_action?: CapaTeamMember['expected_action'];
    position_id?: number | null;
    authority_id?: number | null;
    location_id?: number | null;
}

export const capaInvestigationsService = {
    list: async (params?: { mine?: boolean; inv_status?: InvestigationStatus; q?: string }): Promise<CapaInvestigation[]> => {
        const resp = await apiClient.get('/api/v1/cap/investigations', { params });
        return resp.data?.data ?? [];
    },
    listPaged: async (params?: { mine?: boolean; inv_status?: InvestigationStatus; q?: string; limit?: number; offset?: number }): Promise<{ data: CapaInvestigation[]; total: number }> => {
        const resp = await apiClient.get('/api/v1/cap/investigations', { params });
        const data: CapaInvestigation[] = resp.data?.data ?? [];
        return { data, total: resp.data?.total ?? data.length };
    },
    myInvestigations: async (): Promise<CapaInvestigation[]> => {
        const resp = await apiClient.get('/api/v1/cap/my-investigations');
        return resp.data?.data ?? [];
    },
    get: async (id: number): Promise<CapaInvestigation> => {
        const resp = await apiClient.get(`/api/v1/cap/investigations/${id}`);
        return resp.data?.data;
    },
    sendToInvestigation: async (issueId: number, data: InvestigationCreate): Promise<CapaInvestigation> => {
        const resp = await apiClient.post(`/api/v1/cap/issues/${issueId}/investigations`, data);
        return resp.data?.data;
    },
    close: async (id: number): Promise<CapaInvestigation> => {
        const resp = await apiClient.post(`/api/v1/cap/investigations/${id}/close`);
        return resp.data?.data;
    },
    lock: async (id: number): Promise<CapaInvestigation> => {
        const resp = await apiClient.post(`/api/v1/cap/investigations/${id}/lock`);
        return resp.data?.data;
    },
    unlock: async (id: number): Promise<CapaInvestigation> => {
        const resp = await apiClient.post(`/api/v1/cap/investigations/${id}/unlock`);
        return resp.data?.data;
    },
    transferOwner: async (id: number, data: InvestigationOwnerTransfer): Promise<CapaInvestigation> => {
        const resp = await apiClient.put(`/api/v1/cap/investigations/${id}/owner`, data);
        return resp.data?.data;
    },
    // Team
    listTeam: async (investigationId: number): Promise<CapaTeamMember[]> => {
        const resp = await apiClient.get(`/api/v1/cap/investigations/${investigationId}/team`);
        return resp.data?.data ?? [];
    },
    addTeamMember: async (investigationId: number, data: TeamMemberCreate): Promise<CapaTeamMember> => {
        const resp = await apiClient.post(`/api/v1/cap/investigations/${investigationId}/team`, data);
        return resp.data?.data;
    },
    updateTeamMember: async (investigationId: number, teamRowId: number, data: Partial<TeamMemberCreate>): Promise<void> => {
        await apiClient.put(`/api/v1/cap/investigations/${investigationId}/team/${teamRowId}`, data);
    },
    removeTeamMember: async (investigationId: number, teamRowId: number): Promise<void> => {
        await apiClient.delete(`/api/v1/cap/investigations/${investigationId}/team/${teamRowId}`);
    },
};
